import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  DialogProps,
  DialogContentText,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

export interface CustomPopupProps extends Omit<
  DialogProps,
  "open" | "onClose" | "title"
> {
  open: boolean;
  onClose: () => void;
  title: string;
  text: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  showActions?: boolean;
  confirmButtonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
}

const CustomPopup: React.FC<CustomPopupProps> = ({
  open,
  onClose,
  title,
  text,
  confirmText = "Potwierdź",
  cancelText = "Anuluj",
  onConfirm,
  maxWidth = "sm",
  fullWidth = true,
  showCloseButton = true,
  showActions = true,
  confirmButtonColor = "primary",
  ...dialogProps
}) => {
  const handleConfirm = () => {
    onConfirm?.();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: "11px",
          minWidth: 400,
          background: "#2a2a2a",
          border: "1px solid #474747ff",
        },
      }}
      {...dialogProps}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="div" fontWeight={600}>
            {title}
          </Typography>
          {showCloseButton && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
              sx={{
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          sx={{ color: "text.primary", mb: 2, fontSize: "1rem" }}
        >
          {text}
        </DialogContentText>
      </DialogContent>

      {showActions && (
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{ minWidth: 100 }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={confirmButtonColor}
            sx={{ minWidth: 100 }}
          >
            {confirmText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CustomPopup;
